/* eslint-disable no-lonely-if */
import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { ROLE, STATUS_APPLICATION_SUPPLY, STATUS_APPLICATION_WAGON } from 'helpers/constants';
import { ApplicationSupplyService, ApplicationWagonService } from 'services';
import { useStore } from './store';
import { useGetSearch } from './location';
import { useIsRole, useIsAdmin, useIsManager, useIsUser } from './role';

const DEFAULT_PAGE_SIZE = 20;
const EMPTY_QUERY = Object.freeze({});

function getStoredFilters(key) {
  try {
    const s = localStorage.getItem(key);
    if (!s) return {};
    const parsed = JSON.parse(s);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

/** Exclude empty string/null/undefined so API is not called with e.g. status= */
export function queryForApi(query) {
  return Object.fromEntries(
    Object.entries(query).filter(([, v]) => v !== '' && v != null)
  );
}

const getArchiveDefaultPeriod = () => 'all';
const WAGON_VISIBLE_EMPTY_FIELDS = new Set([
  'status',
  'stationId',
  'productGroup',
  'userConsignee.consigneeId',
  'ton',
]);

const SUPPLY_VISIBLE_EMPTY_FIELDS = new Set([
  'status',
  'applicationWagon.stationId',
  'applicationWagon.productGroup',
  'contractProduct.productId',
  'applicationWagon->userConsignee.consigneeId',
  'applicationWagon.contractId',
  'ton',
]);

const ARCHIVE_VISIBLE_EMPTY_SELECT_FIELDS = new Set([
  'status',
  'applicationWagon.stationId',
  'applicationWagon.productGroup',
  'contractProduct.productId',
  'applicationWagon->userConsignee.consigneeId',
  'applicationWagon.contractId',
]);

const getArchivePeriodDateQuery = (period) => {
  const selected = String(period || getArchiveDefaultPeriod());

  if (selected === 'all') {
    return {};
  }

  const yearNumber = Number(selected);
  if (Number.isNaN(yearNumber)) {
    return {};
  }

  return {
    dateMin: moment().year(yearNumber).startOf('year').valueOf(),
    dateMax: moment().year(yearNumber).endOf('year').valueOf(),
  };
};

const getWagonDefaultDateQuery = (isAdmin, isManager) => (
  (isAdmin || isManager)
    ? {
      dateMin: moment().subtract(1, 'months').startOf('month').valueOf(),
      dateMax: moment().add(1, 'months').startOf('month').endOf('day').valueOf(),
    }
    : {}
);

export const useGetApplicationWagons = (userId, showFilter = false) => {
  const { reload, setUser, setLoading, user } = useStore();
  const isAdmin = useIsAdmin();
  const isManager = useIsManager();
  const storageKey = `granit_filters_wagon_${user?.id ?? 'anon'}`;
  const defaultDateQuery = getWagonDefaultDateQuery(isAdmin, isManager);
  const [data, setData] = useState([]);
  const [query, setQuery] = useState(() => getStoredFilters(storageKey));
  const [filters, setFilters] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  useEffect(() => {
    async function fetchData() {
      const service = new ApplicationWagonService({ setUser });
      const responses = await Promise.all([
        service.filter({ userId }, 'status'),
        service.filter({ userId }, 'stationId'),
        service.filter({ userId }, 'productGroup'),
        service.filter({ userId }, 'userConsignee.consigneeId'),
        {
          field: 'date',
          value: [{ id: moment().startOf('month').valueOf(), time: moment('2015-01-01').startOf('month').valueOf() }],
        },
        service.filter({ userId }, 'ton'),
      ]);

      if (responses.some(value => value !== null)) {
        const result = responses.map(({ field, values = [] }) => {
          const item = {
            alias: field,
            type: 'select',
            field,
            label: '',
            values,
            allowEmpty: WAGON_VISIBLE_EMPTY_FIELDS.has(field),
          };

          switch (field) {
            case 'status':
              item.label = 'Статус';
              item.values = values
                .map(({ DISTINCT }) => ({
                  id: DISTINCT,
                  value: STATUS_APPLICATION_WAGON.toString(DISTINCT),
                }));
              break;
            case 'stationId':
              item.label = 'Станция';
              item.values = values
                .map(value => ({
                  id: value.DISTINCT,
                  value: value['station.value'],
                }))
                .sort((a, b) => (a.value || '').localeCompare(b.value || '', 'ru'));
              break;
            case 'productGroup':
              item.alias = 'productGroup';
              item.label = 'Группа';
              item.values = values
                .map(({ DISTINCT }) => ({ id: DISTINCT, value: DISTINCT }))
                .sort((a, b) => (a.value || '').localeCompare(b.value || '', 'ru'));
              break;
            case 'userConsignee.consigneeId':
              item.alias = 'consigneeId';
              item.label = 'Грузополучатель';
              item.values = values
                .map(value => ({
                  id: value.DISTINCT || 'default',
                  value: value['userConsignee.consignee.value'] || 'Прочие (9999)',
                }))
                .sort((a, b) => (a.value || '').localeCompare(b.value || '', 'ru'));
              break;
            case 'ton':
              item.label = 'Количество тонн';
              item.type = 'range';
              item.values = values.map(({ DISTINCT }) => DISTINCT);
              item.min = item.values.length ? Math.min(...item.values) : 0;
              item.max = item.values.length ? Math.max(...item.values) : 0;
              item.values = values.map(({ DISTINCT }) => ({ id: DISTINCT, value: DISTINCT }));
              break;
            case 'date':
              item.label = 'Дата';
              item.type = 'date-range';
              item.max = moment().add(1, 'year').startOf('month').valueOf();
              item.min = moment('2018-01-01').startOf('month').valueOf();
              item.values = [{ id: moment().startOf('month').valueOf(), time: moment('2015-01-01').startOf('month').valueOf() }];
              break;
            default:
              break;
          }

          return item;
        }).reduce((prev, next) => {
          if (next.values.length >= 1 || next.allowEmpty) {
            prev[next.field] = next;
          }
          return prev;
        }, {});

        const date = {
          alias: 'date',
          type: 'date-range',
          field: 'date',
          label: 'Дата',
          max: moment().add(6, 'month').startOf('month').valueOf(),
          min: moment('2018-01-01').startOf('month').valueOf(),
          values: [{ id: moment().startOf('month').valueOf(), time: moment('2015-01-01').startOf('month').valueOf() }],
        };

        setFilters({ ...result, date });
      }
    }

    if (showFilter) {
      fetchData();
    }
  }, [userId, showFilter, setUser]);

  useEffect(() => {
    let timer;
    let response;
    const effectiveQuery = queryForApi({
      ...defaultDateQuery,
      ...queryForApi(query),
    });

    async function fetchData() {
      const service = new ApplicationWagonService({ setUser });
      const params = { userId, page, limit, ...effectiveQuery };

      timer = setTimeout(async () => {
        setLoading(true);
        response = await service.getApplications(params);
        if (response?.items != null) {
          setData(response.items);
          setTotalCount(response.count ?? 0);
        }
        setLoading(false);
      }, 500);
    }

    fetchData();

    return () => clearTimeout(timer);
  }, [userId, reload, query, page, limit, setLoading, setUser, isAdmin, isManager]);

  useEffect(() => {
    if (showFilter && storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(query));
    }
  }, [query, showFilter, storageKey]);

  const setQueryAndResetPage = (obj) => {
    setQuery(prev => ({ ...prev, ...obj }));
    setPage(0);
  };

  const resetFilters = () => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
    setQuery({});
    setPage(0);
  };

  return [
    data,
    filters,
    query,
    setData,
    setQueryAndResetPage,
    totalCount,
    page,
    limit,
    setPage,
    setLimit,
    resetFilters,
  ];
};

export const useGetApplicationSupplies = (userId, showFilter = false, options = {}) => {
  const { reload, setUser, setLoading, user } = useStore();
  const {
    storageKeySuffix = 'main',
    forcedQuery: forcedQueryOption,
    initialQuery = {},
    isArchive = false,
  } = options;
  const forcedQuery = forcedQueryOption || EMPTY_QUERY;
  const storageUserScope = Number(userId) >= 0
    ? `user_${Number(userId)}`
    : 'common';
  const storageKey = `granit_filters_supply_${storageKeySuffix}_${user?.id ?? 'anon'}_${storageUserScope}`;
  const [data, setData] = useState([]);
  const initialQueryRef = useRef(initialQuery);
  const [query, setQuery] = useState(() => {
    const storedQuery = getStoredFilters(storageKey);
    return {
      ...initialQueryRef.current,
      ...storedQuery,
      ...forcedQuery,
      ...(isArchive && !storedQuery.archivePeriod ? { archivePeriod: getArchiveDefaultPeriod() } : {}),
    };
  });
  const [filters, setFilters] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const isAdmin = useIsAdmin();
  const isManager = useIsManager();

  useEffect(() => {
    const storedQuery = getStoredFilters(storageKey);
    setQuery({
      ...initialQueryRef.current,
      ...storedQuery,
      ...forcedQuery,
      ...(isArchive && !storedQuery.archivePeriod ? { archivePeriod: getArchiveDefaultPeriod() } : {}),
    });
    setPage(0);
  }, [storageKey, isArchive, forcedQuery]);

  useEffect(() => {
    async function fetchData() {
      const service = new ApplicationSupplyService({ setUser });
      const filterMethod = isArchive ? service.filterArchive.bind(service) : service.filter.bind(service);
      const archiveDateQuery = isArchive ? getArchivePeriodDateQuery(query.archivePeriod) : {};
      const baseFilterQuery = queryForApi({ userId, ...forcedQuery, ...archiveDateQuery });
      const requests = [
        filterMethod(baseFilterQuery, 'status'),
        filterMethod(baseFilterQuery, 'applicationWagon.stationId'),
        filterMethod(baseFilterQuery, 'applicationWagon.productGroup'),
        filterMethod(baseFilterQuery, 'contractProduct.productId'),
        filterMethod(baseFilterQuery, 'applicationWagon->userConsignee.consigneeId'),
        filterMethod(baseFilterQuery, 'applicationWagon.contractId'),
        filterMethod(baseFilterQuery, 'applicationWagonId'),
        ...(isArchive ? [filterMethod(baseFilterQuery, 'date')] : [{
          field: 'date',
          value: { id: moment().startOf('month').valueOf(), time: moment('2015-01-01').startOf('month').valueOf() },
        }]),
        filterMethod(baseFilterQuery, 'ton'),
      ];
      const responses = await Promise.all(requests);
      const archiveYears = isArchive
        ? [...new Set(
          (responses.find(r => r?.field === 'date')?.values || [])
            .map(v => v?.DISTINCT)
            .map(v => moment(v))
            .filter(m => m.isValid())
            .map(m => String(m.year())),
        )]
          .sort((a, b) => Number(a) - Number(b))
        : [];

      if (responses.some(value => value !== null)) {
        const result = responses.map(({ field, values = [] }) => {
          const item = {
            alias: field,
            type: 'select',
            field,
            label: '',
            values,
            allowEmpty: SUPPLY_VISIBLE_EMPTY_FIELDS.has(field)
              || (isArchive && ARCHIVE_VISIBLE_EMPTY_SELECT_FIELDS.has(field)),
          };

          switch (field) {
            case 'status':
              item.label = 'Статус';
              item.values = values
                .map(({ DISTINCT }) => ({
                  id: DISTINCT,
                  value: STATUS_APPLICATION_SUPPLY.toString(DISTINCT),
                }));
              break;
            case 'applicationWagon.stationId':
              item.alias = 'stationId';
              item.label = 'Станция';
              item.values = values
                .map(value => ({
                  id: value.DISTINCT,
                  value: value['applicationWagon.station.value'],
                }))
                .sort((a, b) => (a.value || '').localeCompare(b.value || '', 'ru'));
              break;
            case 'applicationWagon.productGroup':
              item.alias = 'productGroup';
              item.label = 'Группа';
              item.values = values
                .map(({ DISTINCT }) => ({ id: DISTINCT, value: DISTINCT }))
                .sort((a, b) => (a.value || '').localeCompare(b.value || '', 'ru'));
              break;
            case 'contractProduct.productId':
              item.alias = 'productId';
              item.label = 'Продукт';
              item.values = values
                .map(value => ({
                  id: value.DISTINCT,
                  value: value['contractProduct.product.value'],
                }))
                .sort((a, b) => (a.value || '').localeCompare(b.value || '', 'ru'));
              break;
            case 'applicationWagon->userConsignee.consigneeId':
              item.alias = 'consigneeId';
              item.label = 'Грузополучатель';
              item.values = values
                .map(value => ({
                  id: value.DISTINCT || 'default',
                  value: value['applicationWagon.userConsignee.consignee.value'] || 'Прочие (9999)',
                }))
                .sort((a, b) => (a.value || '').localeCompare(b.value || '', 'ru'));
              break;
            case 'applicationWagon.contractId':
              item.alias = 'contractId';
              item.label = 'Договор';
              item.values = (values || [])
                .map(v => ({ id: v.id, value: v.value }))
                .sort((a, b) => (a.value || '').localeCompare(b.value || '', 'ru'));
              break;
            case 'applicationWagonId':
              item.alias = 'applicationWagon.id';
              item.label = '№ заявки на вагоны';
              item.values = values
                .map(({ DISTINCT }) => ({ id: DISTINCT, value: DISTINCT }))
                .filter(({ id }) => id !== null && id !== undefined)
                .sort((a, b) => Number(b.id) - Number(a.id));
              break;
            case 'ton':
              item.label = 'Количество тонн';
              item.type = 'range';
              item.values = values.map(({ DISTINCT }) => DISTINCT);
              item.min = item.values.length ? Math.min(...item.values) : 0;
              item.max = item.values.length ? Math.max(...item.values) : 0;
              item.values = values.map(({ DISTINCT }) => ({ id: DISTINCT, value: DISTINCT }));
              break;
            case 'date':
              item.label = 'Дата';
              item.type = 'date-range';
              item.max = moment().add(1, 'year').startOf('month').valueOf();
              item.min = moment('2018-01-01').startOf('month').valueOf();
              item.values = [{ id: moment().startOf('month').valueOf(), time: moment('2015-01-01').startOf('month').valueOf() }];
              break;
            default:
              break;
          }

          return item;
        }).reduce((prev, next) => {
          if (isArchive && next.field === 'date') {
            return prev;
          }
          if (next.values.length >= 1 || next.allowEmpty) {
            prev[next.field] = next;
          }
          return prev;
        }, {});

        const archivePeriod = {
          alias: 'archivePeriod',
          type: 'select',
          field: 'archivePeriod',
          label: 'Период',
          values: [{ id: 'all', value: 'Все' }, ...archiveYears.map(y => ({ id: y, value: y }))],
          emptyValue: false,
          defaultValue: getArchiveDefaultPeriod(),
          neutralValue: 'all',
        };

        if (isArchive) {
          const { ton, ...filtersWithoutTon } = result;
          setFilters({
            ...filtersWithoutTon,
            archivePeriod,
            ...(ton ? { ton } : {}),
          });
        } else {
          setFilters(result);
        }
      }
    }

    if (showFilter) {
      fetchData();
    }
  }, [userId, showFilter, setUser, forcedQuery, isArchive, query.archivePeriod]);

  useEffect(() => {
    let timer;
    let response;
    const archiveDateQuery = isArchive ? getArchivePeriodDateQuery(query.archivePeriod) : {};
    const queryWithoutArchivePeriod = { ...query };
    delete queryWithoutArchivePeriod.archivePeriod;
    const queryWithoutPeriod = { ...queryWithoutArchivePeriod };
    delete queryWithoutPeriod.dateMin;
    delete queryWithoutPeriod.dateMax;
    const normalizedQuery = isArchive
      ? { ...queryWithoutPeriod, ...archiveDateQuery }
      : queryWithoutArchivePeriod;
    const effectiveQuery = queryForApi({
      ...normalizedQuery,
      ...forcedQuery,
    });

    async function fetchData() {
      const service = new ApplicationSupplyService({ setUser });
      const getApplicationsMethod = isArchive
        ? service.getArchiveApplications.bind(service)
        : service.getApplications.bind(service);
      const hasDateFilter = effectiveQuery.dateMin !== undefined || effectiveQuery.dateMax !== undefined;
      const adminDefaultDate = !isArchive && (isAdmin || isManager) && !hasDateFilter
        ? {
          dateMin: moment().subtract(1, 'months').startOf('month').valueOf(),
          dateMax: moment().add(1, 'months').startOf('month').endOf('day').valueOf(),
        }
        : {};
      const params = { userId, page, limit, ...effectiveQuery, ...adminDefaultDate };

      if (Object.keys(effectiveQuery).length) {
        timer = setTimeout(async () => {
          setLoading(true);
          response = await getApplicationsMethod(params);
          if (response?.items != null) {
            setData(response.items);
            setTotalCount(response.count ?? 0);
          }
          setLoading(false);
        }, 500);
      } else {
        setLoading(true);
        response = await getApplicationsMethod(params);
        if (response?.items != null) {
          setData(response.items);
          setTotalCount(response.count ?? 0);
        }
        setLoading(false);
      }
    }

    fetchData();

    return () => clearTimeout(timer);
  }, [userId, reload, query, page, limit, setLoading, setUser, forcedQuery, isAdmin, isManager, isArchive]);

  useEffect(() => {
    if (Object.keys(forcedQuery).length) {
      setQuery(prev => ({ ...prev, ...forcedQuery }));
    }
  }, [forcedQuery]);

  useEffect(() => {
    if (isArchive && !query.archivePeriod) {
      setQuery(prev => ({ ...prev, archivePeriod: getArchiveDefaultPeriod() }));
    }
  }, [isArchive, query.archivePeriod]);

  useEffect(() => {
    if (showFilter && storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(query));
    }
  }, [query, showFilter, storageKey]);

  const setQueryAndResetPage = (obj) => {
    setQuery(prev => ({ ...prev, ...obj }));
    setPage(0);
  };

  const resetFilters = () => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
    setQuery({
      ...initialQueryRef.current,
      ...(isArchive ? { archivePeriod: getArchiveDefaultPeriod() } : {}),
    });
    setPage(0);
  };

  return [
    data,
    filters,
    query,
    setData,
    setQueryAndResetPage,
    totalCount,
    page,
    limit,
    setPage,
    setLimit,
    resetFilters,
  ];
};

const DEFAULT_QUEUE_PAGE_SIZE = 20;

export const useGetApplicationQueue = (userId, showFilter = false) => {
  const { reload, setUser, setLoading } = useStore();
  const [data, setData] = useState([]);
  const [query, setQuery] = useState({});
  const [filters, setFilters] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_QUEUE_PAGE_SIZE);

  useEffect(() => {
    async function fetchData() {
      const service = new ApplicationSupplyService({ setUser });
      const responses = await Promise.all([
        service.filter({ userId, status: STATUS_APPLICATION_SUPPLY.QUEUE }, 'contractProduct.productId'),
      ]);

      if (responses.some(value => value !== null)) {
        const result = responses.map(({ field, values = [] }) => {
          const item = {
            alias: field,
            type: 'select',
            field,
            label: '',
            values,
          };

          switch (field) {
            case 'contractProduct.productId':
              item.alias = 'productId';
              item.label = 'Продукт';
              item.values = values
                .map(value => ({
                  id: value.DISTINCT,
                  value: value['contractProduct.product.value'],
                }))
                .sort((a, b) => (a.value || '').localeCompare(b.value || '', 'ru'));
              break;
            default:
              break;
          }

          return item;
        }).reduce((prev, next) => {
          if (next.values.length >= 1) {
            prev[next.field] = next;
          }
          return prev;
        }, {});

        setFilters(result);
      }
    }

    if (showFilter) {
      fetchData();
    }
  }, [userId, showFilter, setUser]);

  useEffect(() => {
    let timer;
    let response;

    async function fetchData() {
      const service = new ApplicationSupplyService({ setUser });
      const params = { userId, page, limit, ...query };

      if (Object.keys(query).length) {
        timer = setTimeout(async () => {
          setLoading(true);
          response = await service.getApplicationsQueue(params);
          if (response?.items != null) {
            setData(response.items);
            setTotalCount(response.count ?? 0);
          }
          setLoading(false);
        }, 500);
      } else {
        setLoading(true);
        response = await service.getApplicationsQueue(params);
        if (response?.items != null) {
          setData(response.items);
          setTotalCount(response.count ?? 0);
        }
        setLoading(false);
      }
    }

    fetchData();

    return () => clearTimeout(timer);
  }, [userId, reload, query, page, limit, setLoading, setUser]);

  const setQueryAndResetPage = (obj) => {
    setQuery(prev => ({ ...prev, ...obj }));
    setPage(0);
  };

  const resetFilters = () => {
    setQuery({});
    setPage(0);
  };

  return [
    data,
    filters,
    query,
    setData,
    setQueryAndResetPage,
    totalCount,
    page,
    limit,
    setPage,
    setLimit,
    resetFilters,
  ];
};

export const useWebSocketApplicationSupplies = (userId, data, query, setData) => {
  const { socket, setReload } = useStore();
  const isAdminOrManager = useIsRole([ROLE.MANAGER, ROLE.ADMIN]);

  useEffect(() => {
    if (socket) {
      if (isAdminOrManager) {
        socket.on('APPLICATION_NEW_SUPPLY', (application) => {
          if (userId && Number(application.userId) !== Number(userId)) {
            return;
          }
          setReload();
        });
      }

      socket.on('APPLICATION_SUPPLY_STATUS_CHANGED', (application) => {
        if (userId && Number(application.userId) !== Number(userId)) {
          return;
        }
        setReload();
      });
    }

    return () => {
      if (socket) {
        if (isAdminOrManager) {
          socket.off('APPLICATION_NEW_SUPPLY');
        }
        socket.off('APPLICATION_SUPPLY_STATUS_CHANGED');
      }
    };
  }, [userId, socket, isAdminOrManager, setReload]);
};

export const useWebSocketApplicationWagons = (userId, data, query, setData) => {
  const { socket, setReload } = useStore();
  const isAdminOrManager = useIsRole([ROLE.MANAGER, ROLE.ADMIN]);

  useEffect(() => {
    if (socket) {
      if (isAdminOrManager) {
        socket.on('APPLICATION_NEW_WAGON', (application) => {
          if (userId && Number(application.userId) !== Number(userId)) {
            return;
          }
          setReload();
        });
      }

      socket.on('APPLICATION_WAGON_STATUS_CHANGED', (application) => {
        if (userId && Number(application.userId) !== Number(userId)) {
          return;
        }
        setReload();
      });
    }

    return () => {
      if (socket) {
        if (isAdminOrManager) {
          socket.off('APPLICATION_NEW_WAGON');
        }
        socket.off('APPLICATION_WAGON_STATUS_CHANGED');
      }
    };
  }, [userId, socket, isAdminOrManager, setReload]);
};

export const useEditApplication = (data = [], search) => {
  const { user: { id: storeUserId } } = useStore();
  const isAdmin = useIsAdmin();
  const isManager = useIsManager();
  const isUser = useIsUser();
  const { id } = useGetSearch(search);
  const [edit, setEdit] = useState(null);

  useEffect(() => {
    if (data.length && id) {
      const application = data
        .filter(({ userId }) => (isUser && userId === storeUserId) || isAdmin || isManager)
        .find(({ id: _id }) => Number(_id) === Number(id));
      if (application) {
        setEdit(application);
      }
    } else {
      setEdit(null);
    }
  }, [id, data, isUser, isAdmin, isManager, storeUserId]);

  return [edit];
};
